/* OSC v3b — percent-based 2/4 bundle ladder (arm B of the bxgy_bundle A/B test).
 *
 * ONE shared helper for every theme surface (PDP pickers, bamboo buybox,
 * guided builder, inline 1800/temp picker, sidecart preview). The checkout
 * Shopify Function duplicates these EXACT semantics in
 * tools/discount-function/bundle-core.js (parseV3bSpec / v3bIncrementCents /
 * computeV3bDiscount) — the two implementations must agree to the cent.
 *
 * Tag namespace (applied per product via API — the tag is the source of truth):
 *   bundle:v3b:2@<pct2>|4@<pct4>                    (unscoped)
 *   bundle:v3b:<SKU-PREFIX>:2@<pct2>|4@<pct4>       (scoped, longest prefix wins)
 *   pct2 = % off the 2nd set; pct4 = % off each set beyond the 2nd.
 *
 * Math (all increments floored to WHOLE DOLLARS so the advertised % is always
 * honest — never under-delivers):
 *   inc2      = floor(unitPrice × (1 − pct2/100))
 *   incN      = floor(unitPrice × (1 − pct4/100))
 *   total(n)  = unitPrice + inc2 + (n − 2) × incN      (n = 2 or 4)
 * Sanity clamp: any increment below $25 hides that rung / applies no discount
 * (protects clearance-priced colors).
 *
 * Loaded as a blocking script in <head> (theme.liquid), right after the arm
 * assignment, so every consumer — including dynamically-injected scripts like
 * the guided builder — can rely on window.OSCV3B synchronously.
 */
(function () {
  "use strict";

  var MIN_INC_CENTS = 2500; // $25 sanity clamp

  /** Sticky arm for bxgy_bundle, synchronous. Mirrors the theme.liquid <head>
   *  assignment (window.OSC_ARM); falls back to the same localStorage key +
   *  50/50 assignment when called before that script (never happens in
   *  practice, but keeps the accessor total). */
  function arm() {
    if (window.OSC_ARM === "A" || window.OSC_ARM === "B") return window.OSC_ARM;
    var k = "ss_exp_bxgy_bundle";
    var v = null;
    try { v = localStorage.getItem(k); } catch (e) {}
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "B" : "A";
      try { localStorage.setItem(k, v); } catch (e) {}
    }
    return v;
  }

  // "2@35|4@50" -> { pct2: 35, pct4: 50 }; null when malformed.
  function parseSpec(str) {
    var s = String(str || "").trim();
    if (!s) return null;
    var out = {};
    var ok = false;
    s.split("|").forEach(function (seg) {
      var m = String(seg).trim().match(/^(\d+)@(\d+(?:\.\d+)?)$/);
      if (!m) return;
      var units = parseInt(m[1], 10);
      var pct = parseFloat(m[2]);
      if (isNaN(units) || isNaN(pct) || pct <= 0 || pct >= 100) return;
      if (units === 2) { out.pct2 = pct; ok = true; }
      else if (units === 4) { out.pct4 = pct; ok = true; }
    });
    if (!ok || out.pct2 == null || out.pct4 == null) return null;
    return out;
  }

  /** Resolve the raw v3b payload ("2@35|4@50") for a SKU from product tags.
   *  Mirrors the scoped bundle:default resolution exactly: an unscoped payload
   *  starts with a digit; scoped payloads are "<SKU-PREFIX>:2@..|4@..", and the
   *  longest SKU-prefix match wins; unscoped is the fallback. Returns '' when
   *  the product has no (matching) v3b tag. */
  function resolvePayload(tags, sku) {
    var skuU = String(sku || "").trim().toUpperCase();
    var candidates = [];
    (tags || []).forEach(function (tag) {
      var m = String(tag || "").match(/^bundle:v3b:(.+)$/i);
      if (!m) return;
      var payload = m[1].trim();
      if (/^\d/.test(payload)) {
        candidates.push({ key: null, spec: payload });
      } else {
        var x = payload.match(/^(.+?):(\d+@.+)$/);
        if (x) candidates.push({ key: x[1].trim().toUpperCase(), spec: x[2].trim() });
      }
    });
    if (!candidates.length) return "";
    var scoped = candidates.filter(function (c) { return !!c.key; });
    if (scoped.length && skuU) {
      scoped.sort(function (a, b) { return b.key.length - a.key.length; });
      for (var i = 0; i < scoped.length; i++) {
        if (skuU.indexOf(scoped[i].key) === 0) return scoped[i].spec;
      }
    }
    var unscoped = null;
    candidates.some(function (c) { if (!c.key) { unscoped = c; return true; } return false; });
    return unscoped ? unscoped.spec : "";
  }

  // Convenience: tags + sku -> parsed spec (or null).
  function resolveSpec(tags, sku) {
    return parseSpec(resolvePayload(tags, sku));
  }

  /** Whole-dollar increment in CENTS. Integer math (no float drift):
   *  floor(unitCents × (100 − pct) / 10000) dollars. */
  function incCents(unitCents, pct) {
    return Math.floor((Math.round(unitCents) * (100 - pct)) / 10000) * 100;
  }

  /** Full ladder for one variant price + spec. okN=false means that rung is
   *  hidden (clamp) and must not be offered/applied. */
  function ladder(unitCents, spec) {
    if (!spec || !unitCents) return null;
    var inc2 = incCents(unitCents, spec.pct2);
    var incN = incCents(unitCents, spec.pct4);
    var ok2 = inc2 >= MIN_INC_CENTS;
    var ok4 = ok2 && incN >= MIN_INC_CENTS; // total(4) includes inc2, so both must pass
    return {
      pct2: spec.pct2,
      pct4: spec.pct4,
      inc2: inc2,
      incN: incN,
      ok2: ok2,
      ok4: ok4,
      total2: unitCents + inc2,
      total4: unitCents + inc2 + 2 * incN
    };
  }

  // total(n) for n units of ONE variant price (n >= 1). Returns list price
  // when the required rung fails the clamp.
  function totalCents(unitCents, spec, n) {
    var l = ladder(unitCents, spec);
    if (!l || n <= 1) return unitCents * Math.max(1, n);
    var total = unitCents;
    for (var i = 1; i < n; i++) {
      var inc = i === 1 ? (l.ok2 ? l.inc2 : unitCents) : (l.incN >= MIN_INC_CENTS ? l.incN : unitCents);
      total += inc;
    }
    return total;
  }

  // Grouping key: units with the SAME pct pair (i.e. the same line) pool
  // together — mirrors today's bundle:default grouping semantics.
  function specKey(spec) {
    return spec ? spec.pct2 + "@" + spec.pct4 : "";
  }

  /** Discount (cents) for units sharing one pct pair. units = [{cents}] or
   *  plain cents numbers. Sorted by price DESC: the most expensive unit is
   *  full price, the next pays inc2 (computed from ITS OWN price), further
   *  units pay incN — discounts always land on cheaper units. Units whose own
   *  increment fails the $25 clamp get no discount. */
  function groupDiscountCents(unitsCents, spec) {
    if (!spec || !unitsCents || unitsCents.length < 2) return 0;
    var us = unitsCents.slice().sort(function (a, b) { return b - a; });
    var discount = 0;
    for (var i = 1; i < us.length; i++) {
      var pct = i === 1 ? spec.pct2 : spec.pct4;
      var inc = incCents(us[i], pct);
      if (inc < MIN_INC_CENTS) continue;
      discount += us[i] - inc;
    }
    return discount;
  }

  function money(cents) {
    var n = (cents / 100).toFixed(2).replace(/\.00$/, "");
    return "$" + n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // "35% off" / "½ price" (pct2 == 50 headlines the claim as half price).
  function pctLabel(pct) {
    return pct === 50 ? "\u00BD price" : pct + "% off";
  }

  // Anchored upgrade line, shared across pickers:
  //   1 selected  -> "Add a 2nd set — just $48 (35% off) · save $21.95"
  //   2 selected  -> "2 more sets — just $31 each (55% off) · save $77.90"
  function upgradeLine(unitCents, spec, selectedUnits) {
    var l = ladder(unitCents, spec);
    if (!l) return "";
    if (selectedUnits <= 1 && l.ok2) {
      return "Add a 2nd set \u2014 just " + money(l.inc2) + " (" + pctLabel(l.pct2) + ") \u00B7 save " + money(unitCents - l.inc2);
    }
    if (selectedUnits === 2 && l.ok4) {
      return "2 more sets \u2014 just " + money(l.incN) + " each (" + pctLabel(l.pct4) + ") \u00B7 save " + money(2 * (unitCents - l.incN));
    }
    return "";
  }

  // ?bundle= / ?deal= preselect mapping for arm B: 1→1, 2→2, ≥3→4.
  function mapPreselect(n) {
    n = parseInt(n, 10) || 0;
    if (n <= 0) return 0;
    if (n === 1) return 1;
    if (n === 2) return 2;
    return 4;
  }

  window.OSCV3B = {
    MIN_INC_CENTS: MIN_INC_CENTS,
    arm: arm,
    parseSpec: parseSpec,
    resolvePayload: resolvePayload,
    resolveSpec: resolveSpec,
    incCents: incCents,
    ladder: ladder,
    totalCents: totalCents,
    specKey: specKey,
    groupDiscountCents: groupDiscountCents,
    money: money,
    pctLabel: pctLabel,
    upgradeLine: upgradeLine,
    mapPreselect: mapPreselect
  };
})();
